import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";

// Phase 4q portal-ids-polish — drag-drop ID uploader that mirrors
// the CoiUploader pattern (drag-over → brand-red dashed border) and
// adds an image preview thumbnail after pick. Replaces the native
// browser file picker that was the standout "this breaks the design
// system" item in .notes/design-grades/portal/05-ids.md.
//
// Validation runs client-side before the wire hit so customers see
// reasons immediately. The server still enforces (Identifications::
// CreateService), so this is purely UX.

const ACCEPT = ".jpg,.jpeg,.png,.heic,image/jpeg,image/png,image/heic";
const MAX_BYTES = 25 * 1024 * 1024;

const KIND_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "drivers_license", label: "Driver's license" },
  { value: "state_id", label: "State ID" },
  { value: "passport", label: "Passport" },
  { value: "military_id", label: "Military ID" },
  { value: "tribal_id", label: "Tribal ID" },
  { value: "other_government", label: "Other government ID" }
];

function clientValidate(file: File): string | null {
  if (file.size > MAX_BYTES) return "File is larger than 25 MB.";
  if (!/(jpe?g|png|heic)$/i.test(file.name)) {
    return "Only JPG, PNG, or HEIC images are accepted.";
  }
  return null;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

interface Props {
  authorizedIndividualId?: number | null;
}

export function IdUploader({ authorizedIndividualId }: Props): JSX.Element {
  const qc = useQueryClient();
  const [kind, setKind] = useState("drivers_license");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("Please pick an image.");
      const form = new FormData();
      form.append("id_kind", kind);
      form.append("image_front", file);
      if (authorizedIndividualId != null) {
        form.append("authorized_individual_id", String(authorizedIndividualId));
      }
      const csrf =
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? "";
      const res = await fetch("/portal/account/identifications.json", {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, Accept: "application/json" },
        body: form,
        credentials: "same-origin"
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      clearFile();
      qc.invalidateQueries({ queryKey: ["portal", "ids"] });
    }
  });

  function clearFile(): void {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function pick(picked: File): void {
    setLocalError(null);
    const reason = clientValidate(picked);
    if (reason) {
      setLocalError(reason);
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
  }

  function onChange(e: ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0];
    if (picked) pick(picked);
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragging(false);
    const picked = e.dataTransfer.files?.[0];
    if (picked) pick(picked);
  }

  const dropClass = `flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-10 transition ${
    dragging ? "border-brand-red bg-brand-red/5" : "border-zinc-300 bg-zinc-50"
  }`;

  return (
    <form
      data-testid="id-uploader"
      className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div>
        <p className="text-sm font-semibold text-brand-black">Add a new ID</p>
        <p className="mt-1 text-xs text-zinc-500">
          We store your ID encrypted and only release the image to dispatch when verifying
          equipment pickup.
        </p>
      </div>

      <label className="block text-xs font-medium text-zinc-700">
        Kind of ID
        <select
          className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-flag-navy focus:outline-none focus:ring-1 focus:ring-flag-navy"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          disabled={mutation.isPending}
        >
          {KIND_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {file && previewUrl ? (
        <div
          data-testid="id-uploader-preview"
          className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3"
        >
          <img
            src={previewUrl}
            alt="Selected ID preview"
            className="h-16 w-24 rounded border border-zinc-200 object-cover"
          />
          <div className="flex-1 text-xs">
            <p className="font-medium text-brand-black">{file.name}</p>
            <p className="text-zinc-500">{formatBytes(file.size)}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearFile}
            disabled={mutation.isPending}
          >
            Remove
          </Button>
        </div>
      ) : (
        <div
          data-testid="id-uploader-dropzone"
          className={dropClass}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <p className="text-sm text-zinc-700">Drag a photo of your ID here, or</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => inputRef.current?.click()}
            disabled={mutation.isPending}
          >
            Choose image
          </Button>
          <p className="mt-3 text-xs text-zinc-500">JPG, PNG, or HEIC · 25 MB max</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={onChange}
        className="sr-only"
        aria-label="Select ID image"
      />

      {localError ? (
        <div
          role="alert"
          className="rounded-md border border-brand-red/40 bg-brand-red/5 px-3 py-2 text-sm text-brand-red"
        >
          {localError}
        </div>
      ) : null}

      {mutation.isError ? (
        <div
          role="alert"
          className="rounded-md border border-brand-red/40 bg-brand-red/5 px-3 py-2 text-sm text-brand-red"
        >
          {(mutation.error as Error).message}
        </div>
      ) : null}

      <Button type="submit" disabled={!file || mutation.isPending} className="w-full sm:w-auto">
        {mutation.isPending ? "Uploading…" : "Upload ID"}
      </Button>
    </form>
  );
}
